import * as R from 'ramda';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useSnackbar} from 'notistack';
import {ReceiveUserProfileSitesToAppMessage} from '@practicegenius/domain/src/sites-mobile-app/messages';
import {fetchGlobalUserProfile} from '@practicegenius/domain/src/data/users/GlobalUser';
import {postMessageToMobileApp} from '@practicegenius/pg-ui/src/utils/MobileApp';
import {useApi} from '../../http/AuthApiProvider';
import {UserProfile} from './data';
import {atomicUpdate} from '../../shared/utils/atomic-updates';

const userProfileQueryKey = ['global_user_profile'];

export const useUserProfileQuery = () => {
  const {globalUserCoreAuth} = useApi();

  return useQuery({
    queryKey: userProfileQueryKey,
    queryFn: () =>
      fetchGlobalUserProfile(globalUserCoreAuth)
        /**
         * Perform side-effects for mobile app and legacy UI
         * These are done here (instead of queryOptions.onSuccess) to ensure they only run once per user profile fetch and not once per query instance.
         */
        .then(profile => {
          const practiceRecord = profile.practiceRecords[0];
          if (!practiceRecord) {
            return profile;
          }

          // Send user profile to mobile app every time it updates.
          postMessageToMobileApp({
            type: 'receive_user_profile',
            payload: {
              ...profile,
              // TODO this is a temporary fix to get the notification counts to the mobile app
              // This won't work in the long run because a global user can be associated to multiple practice users
              // It works today because we don't yet have any multi-practice users
              // The long-term fix is to either
              // - Update the logic here to reliably get the practice record for the _current_ practice user
              // - Update the mobile app to pull the notification counts from the practice user on its own.
              notificationCounts: practiceRecord.notificationCounts,
              // Send along the cookie so the mobile app can make requests on behalf of this user
              cookie: document.cookie,
            },
          } as ReceiveUserProfileSitesToAppMessage);

          // Update user points for legacy UI
          atomicUpdate(
            'user_points',
            new Intl.NumberFormat().format(practiceRecord.points ?? 0),
          );
          return profile;
        }),
    staleTime: 3 * 60 * 1000, // 3 minutes
  });
};

export const useInvalidateUserProfileQuery = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: userProfileQueryKey,
    });
};

type PrivacyPreferenceKey =
  | 'hide_birthday_from_feed'
  | 'hide_anniversary_from_feed';

export type PrivacyPreferenceData = {
  key: PrivacyPreferenceKey;
  value: Boolean;
};

const useUpdateUserProfileQueryData = () => {
  const queryClient = useQueryClient();
  return (updater: Parameters<typeof queryClient.setQueryData>[1]) =>
    queryClient.setQueryData(userProfileQueryKey, updater);
};

export const useUpdatePrivacyPreferences = () => {
  const {globalUserCoreAuth} = useApi();
  const queryClient = useQueryClient();
  const updateUserProfile = useUpdateUserProfileQueryData();
  const {enqueueSnackbar} = useSnackbar();

  return useMutation<
    unknown,
    unknown,
    PrivacyPreferenceData,
    {previousUserProfile: UserProfile}
  >({
    mutationFn: (preferenceData: PrivacyPreferenceData) =>
      globalUserCoreAuth._unsafe.post(
        `/global_users/hub_settings`,
        preferenceData,
      ),
    onMutate: async ({key, value}) => {
      await queryClient.cancelQueries({queryKey: userProfileQueryKey});
      const previousUserProfile = queryClient.getQueryData(
        userProfileQueryKey,
      ) as UserProfile;
      queryClient.setQueryData(
        userProfileQueryKey,
        R.assocPath(['hubSettings', key], value),
      );
      return {previousUserProfile};
    },
    onSuccess: (_, {key, value}) => {
      updateUserProfile(R.assocPath(['hubSettings', key], value));
    },
    onError: (_, __, context) => {
      if (context) {
        queryClient.setQueryData(
          userProfileQueryKey,
          context.previousUserProfile,
        );
      }
      enqueueSnackbar('Error updating setting.', {
        autoHideDuration: 3000,
        variant: 'error',
      });
    },
  });
};
